<template>
  <!-- z-[3000]: a confirm modal must cover everything that can open it, which includes a
       Vuetify overlay — those start at z-index 2000 and climb by 10 per stacked overlay, so
       anything below that is invisible and unreachable behind a v-dialog (worse when the
       dialog is persistent, which leaves no way back out). Also clears the allocation
       drawer at 60. -->
  <!-- A click on the backdrop or the card keeps focus inside, so Tab stays trapped. -->
  <div v-if="open" class="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 px-4" @mousedown.self.prevent>
    <!-- Keys are handled here and stopped, so Esc or Tab never reaches a v-dialog underneath:
         its own window/document listeners would close it or pull focus back into it. -->
    <div ref="card" class="card max-w-sm w-full outline-none" tabindex="-1" role="dialog" aria-modal="true" :aria-labelledby="titleId" @keydown="onKeydown">
      <h3 :id="titleId" class="text-base font-semibold text-gray-900 m-0">{{ title }}</h3>
      <p class="text-sm text-gray-600 mt-2 mb-0 whitespace-pre-line">{{ message }}</p>
      <p v-if="error" class="text-sm text-red-700 mt-2 mb-0" role="alert">{{ error }}</p>
      <div class="flex flex-wrap gap-3 justify-end pt-4">
        <button ref="cancelButton" class="btn-secondary" type="button" @click="$emit('cancel')">{{ cancelLabel }}</button>
        <button class="btn-danger" type="button" @click="$emit('confirm')">{{ confirmLabel }}</button>
        <!-- An optional constructive way out beside the destructive one: the overbook prompt
             offers "Split across weeks" here. -->
        <button v-if="secondaryLabel" class="btn-primary" type="button" @click="$emit('secondary')">{{ secondaryLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { nextTick, ref, useId, watch } from 'vue';

  import { useFocusReturn } from '@/composables/useFocusReturn';
  import { trapTabTarget } from '@/utils/focusTrap';

  const props = withDefaults(
    defineProps<{
      open: boolean;
      title: string;
      message: string;
      confirmLabel?: string;
      cancelLabel?: string;
      secondaryLabel?: string;
      error?: string | null;
    }>(),
    { confirmLabel: 'Delete', cancelLabel: 'Cancel', secondaryLabel: undefined, error: null }
  );

  const emit = defineEmits<{ confirm: []; cancel: []; secondary: [] }>();

  const titleId = `${useId()}-title`;
  const card = ref<HTMLElement | null>(null);
  const cancelButton = ref<HTMLButtonElement | null>(null);
  const focus = useFocusReturn();

  // Cancel takes focus first: Enter on an open confirm must never run the destructive action.
  watch(
    () => props.open,
    (open, wasOpen) => {
      if (open) {
        focus.remember();
        void nextTick(() => cancelButton.value?.focus());
      } else if (wasOpen) {
        focus.restore();
      }
    },
    { immediate: true }
  );

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      emit('cancel');
      return;
    }
    if (event.key !== 'Tab') return;
    event.preventDefault();
    event.stopPropagation();
    const buttons = [...(card.value?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])];
    trapTabTarget(buttons, document.activeElement as HTMLElement | null, event.shiftKey)?.focus();
  }
</script>
